import {Box, Divider, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {Drawer} from 'presentation/components/Drawer';
import Icon from 'presentation/icons';

import {translations} from 'presentation/translations-new';
import {DateFormatEnum} from 'presentation/constants';
import {formatPrice, formatText, parseDate, stringfyDate} from 'presentation/utils';
import {TherapyDetailsDrawerProps} from './types';
import {convertBRLCurrencyToNumber} from 'presentation/utils/convertBRLCurrencyToNumber';

const {exclude, editInformation} = translations['pt-br'].commons;
const {sessions, therapyType, therapyDetail, totalSessions} =
	translations['pt-br'].privateFlow.refund.therapies;
const {dateField, valueField} = translations['pt-br'].privateFlow.refund.holderDataPage.requestInfo;

export const TherapyDetailsDrawer = ({
	isOpen,
	onClose,
	therapyData,
	onRemoveTherapy,
	onEditTherapy,
}: TherapyDetailsDrawerProps) => {
	const theme = useTheme();

	if (!therapyData) return null;

	const totalValue = therapyData.sessions.reduce((acc, session) => {
		if (session.sessionValue) {
			return acc + convertBRLCurrencyToNumber(session.sessionValue);
		}
		return acc;
	}, 0);

	const formatDate = (dateString?: string) => {
		if (!dateString) return '-';

		const date = parseDate(dateString);
		if (!date) return dateString;

		return stringfyDate(date, DateFormatEnum.DATE_FORMAT_8);
	};

	const handleEditClick = () => {
		onEditTherapy();
		onClose();
	};

	const handleDeleteClick = () => {
		onRemoveTherapy();
	};

	return (
		<Drawer.Root
			open={isOpen}
			onClose={onClose}
			onOpen={() => null}
			width="685px"
			customMaxHeight="100%">
			<Drawer.Header onClose={onClose} title={therapyDetail} />
			<Drawer.Content>
				<Stack gap={2} sx={{mt: 3}}>
					<Stack direction="row" gap={1}>
						<Icon name="list-filled" size={20} color={theme.palette.primary.main} />
						<Box>
							<Typography variant="body2" color="text.secondary">
								{therapyType}
							</Typography>
							<Typography variant="subtitle1">
								{formatText(therapyData.therapyType?.description || '-')}
							</Typography>
						</Box>
					</Stack>

					<Stack direction="row" gap={1}>
						<Icon
							name="attach-money-filled"
							size={20}
							color={theme.palette.primary.main}
						/>
						<Box>
							<Typography variant="body2" color="text.secondary">
								{totalSessions}
							</Typography>
							<Typography variant="subtitle1">{formatPrice(totalValue)}</Typography>
						</Box>
					</Stack>

					<Box>
						<Typography variant="subtitle2" mb={2} color="text.secondary">
							{sessions}
						</Typography>
						<Stack gap={1}>
							{therapyData.sessions.map((session, index: number) => (
								<Fragment key={index}>
									<Stack direction="row" spacing={2}>
										<Stack direction="column" flex={1}>
											<Typography variant="body2" color="text.secondary">
												{dateField}
											</Typography>
											<Typography variant="subtitle1">
												{formatDate(session?.serviceDate)}
											</Typography>
										</Stack>
										<Stack direction="column" flex={1}>
											<Typography variant="body2" color="text.secondary">
												{valueField}
											</Typography>
											<Typography variant="subtitle1">
												{session.sessionValue
													? formatPrice(session.sessionValue)
													: '-'}
											</Typography>
										</Stack>
									</Stack>
									{index < therapyData.sessions.length - 1 ? <Divider /> : null}
								</Fragment>
							))}
						</Stack>
					</Box>
				</Stack>
			</Drawer.Content>
			<Drawer.Footer
				firstButtonTitle={exclude}
				firstButtonOnClick={handleDeleteClick}
				firstButtonColor="error"
				secondButtonTitle={editInformation}
				secondButtonOnClick={handleEditClick}
				canStacks
			/>
		</Drawer.Root>
	);
};
